import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { QRCodeCanvas } from 'qrcode.react';

/**
 * Coach: Group management – create/edit groups, QR code, member list.
 * Route: /coach/gruppen
 */
const CoachGroups = () => {
    const { fetchWithAuth } = useContext(AuthContext);
    const navigate = useNavigate();
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [groupDetail, setGroupDetail] = useState(null);

    useEffect(() => { loadGroups(); }, []);

    const loadGroups = async () => {
        setLoading(true);
        try {
            const res = await fetchWithAuth('/api/coach/groups/');
            const data = await res.json();
            setGroups(Array.isArray(data) ? data : data.results || []);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    };

    const loadDetail = async (id) => {
        setSelectedGroup(id);
        const res = await fetchWithAuth(`/api/coach/groups/${id}/`);
        if (res.ok) setGroupDetail(await res.json());
    };

    const regenerateCode = async () => {
        if (!selectedGroup) return;
        const res = await fetchWithAuth(`/api/coach/groups/${selectedGroup}/regenerate-code/`, { method: 'POST' });
        if (res.ok) {
            const data = await res.json();
            setGroupDetail(prev => prev ? { ...prev, invite_code: data.invite_code } : prev);
            loadGroups();
        }
    };

    const kickMember = async (userId) => {
        if (!selectedGroup || !confirm('Azubi aus Gruppe entfernen?')) return;
        await fetchWithAuth(`/api/coach/groups/${selectedGroup}/kick/${userId}/`, { method: 'DELETE' });
        loadDetail(selectedGroup);
    };

    const downloadQRCode = () => {
        const canvas = document.getElementById('group-qr-code');
        if (!canvas) return;
        const url = canvas.toDataURL('image/png');
        const link = document.createElement('a');
        link.download = `qr-code-${groupDetail.name.toLowerCase().replace(/\s+/g, '-')}.png`;
        link.href = url;
        link.click();
    };

    const inviteUrl = groupDetail ? `${window.location.origin}/join/${groupDetail.invite_code}` : '';

    if (loading) return (
        <div className="flex items-center justify-center min-h-[60vh]">
            <div className="w-8 h-8 border-4 border-plantlix-500 border-t-transparent rounded-full animate-spin" />
        </div>
    );

    return (
        <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">
            <button onClick={() => navigate('/coach')} className="text-sm text-gray-500 dark:text-gray-400 hover:text-plantlix-500 transition flex items-center gap-1">
                ← Dashboard
            </button>

            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">⚙️ Gruppen verwalten</h1>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Left: group list */}
                <div className="space-y-2">
                    {groups.map(g => (
                        <button
                            key={g.id}
                            onClick={() => loadDetail(g.id)}
                            className={`w-full text-left px-4 py-3 rounded-xl border transition ${
                                selectedGroup === g.id
                                    ? 'border-plantlix-400 bg-plantlix-50 dark:bg-plantlix-900/20'
                                    : 'border-gray-100 dark:border-white/5 bg-white dark:bg-[#1a1a1a] hover:border-gray-200'
                            }`}
                        >
                            <p className="font-bold text-gray-900 dark:text-white text-sm">{g.name}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">{g.member_count} Mitglieder</p>
                        </button>
                    ))}
                </div>

                {/* Right: group detail */}
                <div className="md:col-span-2">
                    {!groupDetail ? (
                        <div className="text-center py-16 text-gray-400">
                            <p className="text-4xl mb-3">👈</p>
                            <p className="text-sm">Wähle eine Gruppe aus</p>
                        </div>
                    ) : (
                        <div className="bg-white dark:bg-[#1a1a1a] border border-gray-100 dark:border-white/5 rounded-2xl p-6 space-y-6">
                            <div>
                                <h2 className="font-bold text-lg text-gray-900 dark:text-white">{groupDetail.name}</h2>
                                {groupDetail.description && <p className="text-sm text-gray-500 mt-1">{groupDetail.description}</p>}
                            </div>

                            {/* Invite code */}
                            <div className="bg-plantlix-50 dark:bg-plantlix-900/10 border border-plantlix-200 dark:border-plantlix-800 rounded-xl p-4 space-y-3">
                                <p className="text-sm font-semibold text-plantlix-700 dark:text-plantlix-400">🔗 Einladungslink</p>
                                <div className="flex gap-2">
                                    <input
                                        readOnly value={inviteUrl}
                                        className="flex-1 px-3 py-2 rounded-lg bg-white dark:bg-black/30 border border-plantlix-200 dark:border-plantlix-800 text-sm text-gray-800 dark:text-gray-200 font-mono text-xs"
                                    />
                                    <button
                                        onClick={() => { navigator.clipboard.writeText(inviteUrl); }}
                                        className="px-3 py-2 rounded-lg bg-plantlix-500 hover:bg-plantlix-600 text-white text-xs font-bold transition"
                                    >
                                        Kopieren
                                    </button>
                                </div>
                                <div className="flex items-center gap-3">
                                    <p className="text-xs text-gray-500">Code: <code className="font-mono text-plantlix-600 dark:text-plantlix-400">{groupDetail.invite_code}</code></p>
                                    <button onClick={regenerateCode} className="text-xs text-amber-600 dark:text-amber-400 hover:underline">Neuen Code generieren</button>
                                </div>
                                
                                {/* QR Code Implementation */}
                                <div className="flex flex-col items-center justify-center py-6 bg-white dark:bg-black/20 rounded-lg border border-gray-200 dark:border-white/5">
                                    <div className="p-3 bg-white rounded-xl shadow-sm border border-gray-100">
                                        <QRCodeCanvas 
                                            id="group-qr-code"
                                            value={inviteUrl} 
                                            size={160}
                                            level="H"
                                            includeMargin={false}
                                            imageSettings={{
                                                src: "/apple-touch-icon.png", // Attempt to use app icon if available
                                                x: undefined,
                                                y: undefined,
                                                height: 32,
                                                width: 32,
                                                excavate: true,
                                            }}
                                        />
                                    </div>
                                    <p className="text-[10px] text-gray-400 mt-3 mb-2 italic">Zum Beitreten einfach scannen</p>
                                    <button 
                                        onClick={downloadQRCode}
                                        className="text-xs text-plantlix-600 dark:text-plantlix-400 font-semibold hover:underline flex items-center gap-1"
                                    >
                                        <span>📥 QR-Code herunterladen</span>
                                    </button>
                                </div>
                            </div>

                            {/* Members */}
                            <div>
                                <h3 className="font-semibold text-sm text-gray-700 dark:text-gray-300 mb-3">
                                    Mitglieder ({groupDetail.memberships?.length || 0})
                                </h3>
                                {(!groupDetail.memberships || groupDetail.memberships.length === 0) && (
                                    <p className="text-xs text-gray-400 py-4 text-center">Noch keine Mitglieder.</p>
                                )}
                                <div className="space-y-2">
                                    {(groupDetail.memberships || []).map(m => (
                                        <div key={m.id} className="flex items-center gap-3 px-3 py-2 rounded-lg bg-gray-50 dark:bg-white/5">
                                            <div className="w-8 h-8 rounded-full bg-plantlix-100 dark:bg-plantlix-900/30 flex items-center justify-center text-sm font-bold text-plantlix-600">
                                                {(m.apprentice?.first_name?.[0] || m.apprentice?.username?.[0] || '?').toUpperCase()}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-semibold text-gray-800 dark:text-gray-200 truncate">
                                                    {m.apprentice?.first_name ? `${m.apprentice.first_name} ${m.apprentice.last_name}` : m.apprentice?.username}
                                                </p>
                                                <p className="text-[10px] text-gray-400">
                                                    {m.consent_given ? '✅ DSGVO zugestimmt' : '⏳ Zustimmung ausstehend'}
                                                </p>
                                            </div>
                                            <button onClick={() => kickMember(m.apprentice?.id)} className="text-xs text-red-400 hover:text-red-600 transition">
                                                Entfernen
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Trainers */}
                            <div>
                                <h3 className="font-semibold text-sm text-gray-700 dark:text-gray-300 mb-2">Ausbilder</h3>
                                <div className="flex flex-wrap gap-2">
                                    {(groupDetail.trainers || []).map(t => (
                                        <span key={t.id} className="px-3 py-1 rounded-lg bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-400 text-xs font-semibold">
                                            {t.first_name ? `${t.first_name} ${t.last_name}` : t.username}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CoachGroups;
